import './Quizzes.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchQuizzes } from '../../services/quizService';
import { getMyQuizResults } from '../../services/quizResultsService';
import { isLoggedIn } from '../../services/authService';
import Pagination from '../Pagination/Pagination';

const QUIZZES_PER_PAGE = 12;

function Quizzes() {
    const [searchTerm, setSearchTerm] = useState('');
    const [completionFilter, setCompletionFilter] = useState('all'); // 'all' | 'completed' | 'incomplete'
    const [quizzes, setQuizzes] = useState([]);
    const [myResults, setMyResults] = useState({}); // quizId → result object
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const navigate = useNavigate();

    const loadQuizzes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchQuizzes(currentPage, QUIZZES_PER_PAGE);
            let list = [];
            if (data.content) {
                list = data.content;
                setTotalElements(data.page?.totalElements || data.content.length);
                setTotalPages(data.page?.totalPages || Math.ceil((data.page?.totalElements || data.content.length) / QUIZZES_PER_PAGE));
            } else if (Array.isArray(data)) {
                list = data;
                setTotalElements(data.length);
                setTotalPages(1);
            }
            setQuizzes(list);

            // Fetch all results for logged-in users in a single request
            if (isLoggedIn()) {
                const results = await getMyQuizResults();
                const resultMap = Object.fromEntries(results.map(r => [r.quizId, r]));
                setMyResults(resultMap);
            }
        } catch (err) {
            console.error('Error loading quizzes:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage]);

    useEffect(() => {
        loadQuizzes();
    }, [loadQuizzes]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleQuizClick = (quizId) => {
        navigate(`/kvizy/${quizId}`);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Client-side filtering
    const filteredQuizzes = quizzes.filter(quiz => {
        if (searchTerm.trim()) {
            const s = searchTerm.toLowerCase();
            if (!quiz.title?.toLowerCase().includes(s) && !quiz.shortDescription?.toLowerCase().includes(s)) return false;
        }
        if (completionFilter === 'completed' && !myResults[quiz.id]) return false;
        if (completionFilter === 'incomplete' && myResults[quiz.id]) return false;
        return true;
    });

    return (
        <div className="quizzes-page">
            <div className="quizzes-container">
                <h1 className="page-title">Kvízy</h1>

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat kvízy podle názvu nebo popisu..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {isLoggedIn() && (
                    <div className="completion-filter">
                        {[
                            { key: 'all', label: 'Všechny' },
                            { key: 'completed', label: '✓ Splněné' },
                            { key: 'incomplete', label: '○ Nesplněné' },
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                className={`filter-tab${completionFilter === key ? ' active' : ''}`}
                                onClick={() => setCompletionFilter(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {error && (
                    <div className="no-results">
                        <p>{error}</p>
                    </div>
                )}

                {loading ? (
                    <div className="no-results">
                        <p>Načítání kvízů...</p>
                    </div>
                ) : (
                    <div className="quizzes-grid">
                        {filteredQuizzes.length > 0 ? (
                            filteredQuizzes.map(quiz => {
                                const result = myResults[quiz.id];
                                const pct = result ? Math.round((result.correctAnswers / result.totalQuestions) * 100) : null;
                                return (
                                    <div
                                        key={quiz.id}
                                        className={`quiz-tile${result ? ' quiz-tile--completed' : ''}`}
                                        onClick={() => handleQuizClick(quiz.id)}
                                    >
                                        {result && (
                                            <div className="quiz-result-badge">
                                                ✓ {result.correctAnswers}/{result.totalQuestions} ({pct}%)
                                            </div>
                                        )}
                                        <h2 className="quiz-title">{quiz.title}</h2>
                                        <p className="quiz-description">{quiz.shortDescription}</p>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="no-results">
                                <p>{searchTerm.trim() || completionFilter !== 'all' ? 'Nebyly nalezeny žádné kvízy odpovídající filtru.' : 'Zatím nejsou žádné kvízy.'}</p>
                            </div>
                        )}
                    </div>
                )}

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    onPageChange={handlePageChange}
                    itemLabel="kvízů"
                />
            </div>
        </div>
    );
}

export default Quizzes;
